/**
 * The measured duration of each TTS chunk, which the seek bar sums into a
 * document timeline before any chunk has been fetched.
 *
 * An `atomKvsSync` store with a decoding schema that also accepts the shape the
 * pre-atom zustand persistence wrote.
 */
import { RegistryContext, useAtomSet, useAtomValue } from '@effect/atom-react';
import { Schema, SchemaTransformation, Struct } from 'effect';
import type { AtomRegistry } from 'effect/unstable/reactivity';
import { use, useMemo } from 'react';
import { atomKvsSync } from '../atoms/kvs';

const ttsChunkDurationCacheStateSchema = Schema.Struct({
  data: Schema.Record(Schema.String, Schema.Record(Schema.String, Schema.Int)),
});
type TtsChunkDurationCacheState = typeof ttsChunkDurationCacheStateSchema.Type;
const emptyTtsChunkDurationCache: TtsChunkDurationCacheState = { data: {} };

const partialTtsChunkDurationCacheSchema =
  ttsChunkDurationCacheStateSchema.mapFields(Struct.map(Schema.optional));
export const ttsChunkAudioDurationMsCacheAtom = atomKvsSync({
  key: 'tts-chunk-audio-duration-ms-cache',
  schema: Schema.Union([
    Schema.Struct({
      state: partialTtsChunkDurationCacheSchema,
      version: Schema.Int,
    }),
    partialTtsChunkDurationCacheSchema,
  ]).pipe(
    Schema.decodeTo(
      ttsChunkDurationCacheStateSchema,
      SchemaTransformation.transform({
        decode: (stored) => ({
          ...emptyTtsChunkDurationCache,
          ...('state' in stored ? stored.state : stored),
        }),
        encode: (state) => state,
      }),
    ),
  ),
  defaultValue: () => emptyTtsChunkDurationCache,
});

const lookupTtsChunkAudioDurationMs = (
  data: TtsChunkDurationCacheState['data'],
  ttsId: string,
  voice: string,
): number | undefined => {
  const ttsState = data[ttsId];
  if (!ttsState) return;
  // A cached duration of 0 must hit, not be treated as a miss.
  const exact = ttsState[voice];
  if (exact !== undefined) return exact;
  return Object.values(ttsState).find((ms) => ms !== undefined);
};

export const setTtsChunkAudioDurationMs = (
  registry: AtomRegistry.AtomRegistry,
  ttsId: string,
  voice: string,
  ms: number,
): void => {
  const { data } = registry.get(ttsChunkAudioDurationMsCacheAtom);
  registry.set(ttsChunkAudioDurationMsCacheAtom, {
    data: { ...data, [ttsId]: { ...data[ttsId], [voice]: ms } },
  });
};

export const resetTtsChunkAudioDurationMsCache = (
  registry: AtomRegistry.AtomRegistry,
): void => {
  registry.set(ttsChunkAudioDurationMsCacheAtom, emptyTtsChunkDurationCache);
};

type TtsChunkAudioDurationMsCacheStore = {
  data: TtsChunkDurationCacheState['data'];
  getMs: (ttsId: string, voice: string) => number | undefined;
  setMs: (ttsId: string, voice: string, ms: number) => void;
};

export function useTtsChunkAudioDurationMsCacheStore<T>(
  selector: (store: TtsChunkAudioDurationMsCacheStore) => T,
): T {
  const registry = use(RegistryContext);
  const setState = useAtomSet(ttsChunkAudioDurationMsCacheAtom);
  // STABLE and registry-live, like the zustand actions they replace: selecting
  // `s.getMs` must not resubscribe consumers to cache writes (it sits in the
  // seek hook's prefix-sums memo deps), and calls must read current data.
  const actions = useMemo(
    () => ({
      getMs: (ttsId: string, voice: string) =>
        lookupTtsChunkAudioDurationMs(
          registry.get(ttsChunkAudioDurationMsCacheAtom).data,
          ttsId,
          voice,
        ),
      setMs: (ttsId: string, voice: string, ms: number) => {
        setState((current) => ({
          data: {
            ...current.data,
            [ttsId]: { ...current.data[ttsId], [voice]: ms },
          },
        }));
      },
    }),
    [registry, setState],
  );
  return useAtomValue(ttsChunkAudioDurationMsCacheAtom, (state) =>
    selector({ data: state.data, ...actions }),
  );
}
